import React, { useEffect, useState } from 'react';
import { adminAcademyAPI } from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';
import {
  AcademicCapIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  PhotoIcon,
  DocumentArrowUpIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';

const emptyCourse = {
  title: '',
  description: '',
  thumbnailUrl: '',
  sortOrder: 0,
  isPublished: false,
  estimatedMinutes: ''
};

const emptyLesson = {
  title: '',
  content: '',
  videoUrl: '',
  attachmentUrl: '',
  sortOrder: 0,
  durationMinutes: ''
};

// Pasting "youtube.com/watch?v=..." without a scheme fails the server's URI validation.
const normalizeUrl = (value) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const toNullableNumber = (value) =>
  value === '' || value === null || value === undefined ? null : Number(value);

const buildCoursePayload = (form) => ({
  title: form.title.trim(),
  description: form.description || '',
  thumbnailUrl: normalizeUrl(form.thumbnailUrl),
  sortOrder: Number(form.sortOrder) || 0,
  isPublished: !!form.isPublished,
  estimatedMinutes: toNullableNumber(form.estimatedMinutes)
});

const buildLessonPayload = (form) => ({
  title: form.title.trim(),
  content: form.content || '',
  videoUrl: normalizeUrl(form.videoUrl),
  attachmentUrl: normalizeUrl(form.attachmentUrl),
  sortOrder: Number(form.sortOrder) || 0,
  durationMinutes: toNullableNumber(form.durationMinutes)
});

const AdminAcademy = () => {
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [courseForm, setCourseForm] = useState(null);
  const [lessonForm, setLessonForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const res = await adminAcademyAPI.listCourses();
      setCourses(res.data.courses || []);
    } catch (error) {
      toast.error('Failed to load academy courses');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file, onUrl) => {
    try {
      setUploading(true);
      const res = await adminAcademyAPI.uploadFile(file);
      onUrl(res.data.url);
      toast.success('File uploaded');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const saveCourse = async () => {
    if (!courseForm?.title?.trim()) {
      toast.error('Course title is required');
      return;
    }
    try {
      setSaving(true);
      const payload = buildCoursePayload(courseForm);
      if (courseForm.id) {
        await adminAcademyAPI.updateCourse(courseForm.id, payload);
        toast.success('Course updated');
      } else {
        await adminAcademyAPI.createCourse(payload);
        toast.success('Course created');
      }
      setCourseForm(null);
      await loadCourses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const deleteCourse = async (id) => {
    if (!window.confirm('Delete this course and all its lessons?')) return;
    try {
      await adminAcademyAPI.deleteCourse(id);
      toast.success('Course deleted');
      if (expandedId === id) setExpandedId(null);
      await loadCourses();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const saveLesson = async (courseId) => {
    if (!lessonForm?.title?.trim()) {
      toast.error('Lesson title is required');
      return;
    }
    try {
      setSaving(true);
      const payload = buildLessonPayload(lessonForm);
      if (lessonForm.id) {
        await adminAcademyAPI.updateLesson(lessonForm.id, payload);
        toast.success('Lesson updated');
      } else {
        await adminAcademyAPI.createLesson(courseId, payload);
        toast.success('Lesson created');
      }
      setLessonForm(null);
      await loadCourses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save lesson');
    } finally {
      setSaving(false);
    }
  };

  const deleteLesson = async (id) => {
    if (!window.confirm('Delete this lesson?')) return;
    try {
      await adminAcademyAPI.deleteLesson(id);
      toast.success('Lesson deleted');
      await loadCourses();
    } catch (error) {
      toast.error('Failed to delete lesson');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AcademicCapIcon className="h-6 w-6 sm:h-7 sm:w-7 text-primary-600 shrink-0" />
            Poultry Academy
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            First 2 published courses (by sort order) are free. The rest require a subscription.
          </p>
        </div>
        <button
          onClick={() => setCourseForm({ ...emptyCourse, sortOrder: courses.length })}
          className="btn-primary inline-flex items-center justify-center gap-2 text-sm w-full sm:w-auto shrink-0"
        >
          <PlusIcon className="h-4 w-4" />
          New course
        </button>
      </div>

      {courseForm && (
        <CourseEditor
          form={courseForm}
          setForm={setCourseForm}
          onSave={saveCourse}
          onCancel={() => setCourseForm(null)}
          onUpload={handleUpload}
          uploading={uploading}
          saving={saving}
        />
      )}

      {courses.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center text-gray-500">
          No courses yet. Create your first course to get started.
        </div>
      ) : (
        <div className="space-y-4">
          {courses.map((course, index) => (
            <div key={course.id} className="rounded-xl border bg-white overflow-hidden shadow-sm">
              <div className="flex items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4">
                {course.thumbnailUrl ? (
                  <img src={course.thumbnailUrl} alt="" className="h-12 w-16 sm:h-14 sm:w-20 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="h-12 w-16 sm:h-14 sm:w-20 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                    <AcademicCapIcon className="h-5 w-5 sm:h-6 sm:w-6 text-primary-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900 break-words">{course.title}</h3>
                    {course.isPublished ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Published</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Draft</span>
                    )}
                    {index < 2 && course.isPublished && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Free tier</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-2 sm:truncate">{course.description || 'No description'}</p>
                  <p className="text-xs text-gray-400 mt-1 break-all">
                    Order {course.sortOrder} · {course.lessons?.length || 0} lessons · /academy/{course.slug}
                  </p>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                  <button
                    onClick={() => setCourseForm({ ...course, estimatedMinutes: course.estimatedMinutes ?? '' })}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                    title="Edit course"
                  >
                    <PencilSquareIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => deleteCourse(course.id)}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                    title="Delete course"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setExpandedId(expandedId === course.id ? null : course.id)}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                  >
                    {expandedId === course.id ? (
                      <ChevronUpIcon className="h-5 w-5" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {expandedId === course.id && (
                <div className="border-t bg-gray-50 p-3 sm:p-4 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="font-medium text-gray-900">Lessons</h4>
                    <button
                      onClick={() => setLessonForm({
                        ...emptyLesson,
                        sortOrder: course.lessons?.length || 0,
                        _courseId: course.id
                      })}
                      className="text-sm inline-flex items-center gap-1 text-primary-700 hover:text-primary-800 font-medium shrink-0"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add lesson
                    </button>
                  </div>

                  {lessonForm?._courseId === course.id && (
                    <LessonEditor
                      form={lessonForm}
                      setForm={setLessonForm}
                      onSave={() => saveLesson(course.id)}
                      onCancel={() => setLessonForm(null)}
                      onUpload={handleUpload}
                      uploading={uploading}
                      saving={saving}
                    />
                  )}

                  {(course.lessons || []).length === 0 ? (
                    <p className="text-sm text-gray-500">No lessons yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {(course.lessons || []).map((lesson, li) => (
                        <li key={lesson.id} className="flex items-center justify-between gap-2 rounded-lg bg-white border px-3 sm:px-4 py-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 break-words">
                              {li + 1}. {lesson.title}
                            </p>
                            <p className="text-xs text-gray-500">
                              Order {lesson.sortOrder}
                              {lesson.durationMinutes ? ` · ${lesson.durationMinutes} min` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => setLessonForm({
                                ...lesson,
                                durationMinutes: lesson.durationMinutes ?? '',
                                _courseId: course.id
                              })}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteLesson(lesson.id)}
                              className="p-1.5 rounded hover:bg-red-50 text-red-600"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CourseEditor = ({ form, setForm, onSave, onCancel, onUpload, uploading, saving }) => (
  <div className="rounded-xl border bg-white p-4 sm:p-6 space-y-4 shadow-sm">
    <h3 className="font-semibold text-gray-900">{form.id ? 'Edit course' : 'New course'}</h3>
    <div className="grid sm:grid-cols-2 gap-4">
      <label className="block sm:col-span-2">
        <span className="text-sm font-medium text-gray-700">Title</span>
        <input
          className="mt-1 w-full rounded-lg border-gray-300"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
        />
      </label>
      <label className="block sm:col-span-2">
        <span className="text-sm font-medium text-gray-700">Description</span>
        <textarea
          className="mt-1 w-full rounded-lg border-gray-300"
          rows={3}
          value={form.description || ''}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-gray-700">Sort order</span>
        <input
          type="number"
          className="mt-1 w-full rounded-lg border-gray-300"
          value={form.sortOrder}
          onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-gray-700">Est. minutes</span>
        <input
          type="number"
          className="mt-1 w-full rounded-lg border-gray-300"
          value={form.estimatedMinutes}
          onChange={(e) => setForm({ ...form, estimatedMinutes: e.target.value })}
        />
      </label>
      <label className="flex items-center gap-2 sm:col-span-2">
        <input
          type="checkbox"
          checked={!!form.isPublished}
          onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
        />
        <span className="text-sm text-gray-700">Published (visible on academy page)</span>
      </label>
      <div className="sm:col-span-2">
        <span className="text-sm font-medium text-gray-700">Thumbnail</span>
        <div className="mt-1 flex items-center gap-3 flex-wrap">
          {form.thumbnailUrl && (
            <img src={form.thumbnailUrl} alt="" className="h-16 w-24 rounded object-cover" />
          )}
          <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-gray-50 text-sm">
            <PhotoIcon className="h-4 w-4" />
            {uploading ? 'Uploading…' : 'Upload image'}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onUpload(file, (url) => setForm((f) => ({ ...f, thumbnailUrl: url })));
              }}
            />
          </label>
        </div>
      </div>
    </div>
    <div className="flex flex-col-reverse sm:flex-row gap-2">
      <button onClick={onSave} disabled={saving} className="btn-primary text-sm w-full sm:w-auto">
        {saving ? 'Saving…' : 'Save course'}
      </button>
      <button onClick={onCancel} className="btn-outline text-sm w-full sm:w-auto">Cancel</button>
    </div>
  </div>
);

const LessonEditor = ({ form, setForm, onSave, onCancel, onUpload, uploading, saving }) => (
  <div className="rounded-lg border bg-white p-3 sm:p-4 space-y-3">
    <h4 className="font-medium text-gray-900">{form.id ? 'Edit lesson' : 'New lesson'}</h4>
    <input
      className="w-full rounded-lg border-gray-300 text-sm"
      placeholder="Lesson title"
      value={form.title}
      onChange={(e) => setForm({ ...form, title: e.target.value })}
    />
    <textarea
      className="w-full rounded-lg border-gray-300 text-sm"
      rows={6}
      placeholder="Lesson content (text, instructions, notes…)"
      value={form.content || ''}
      onChange={(e) => setForm({ ...form, content: e.target.value })}
    />
    <input
      className="w-full rounded-lg border-gray-300 text-sm"
      placeholder="Video URL (YouTube, Vimeo, etc.)"
      value={form.videoUrl || ''}
      onChange={(e) => setForm({ ...form, videoUrl: e.target.value })}
    />
    <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-end">
      <label className="block min-w-0">
        <span className="text-xs text-gray-500">Sort order</span>
        <input
          type="number"
          className="mt-0.5 w-full sm:w-24 rounded-lg border-gray-300 text-sm"
          value={form.sortOrder}
          onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
        />
      </label>
      <label className="block min-w-0">
        <span className="text-xs text-gray-500">Duration (min)</span>
        <input
          type="number"
          className="mt-0.5 w-full sm:w-24 rounded-lg border-gray-300 text-sm"
          value={form.durationMinutes}
          onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })}
        />
      </label>
      <label className="col-span-2 sm:col-auto flex sm:inline-flex items-center justify-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-gray-50 text-sm">
        <DocumentArrowUpIcon className="h-4 w-4 shrink-0" />
        {uploading ? 'Uploading…' : 'Attach PDF'}
        <input
          type="file"
          accept="application/pdf"
          className="hidden"
          disabled={uploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUpload(file, (url) => setForm((f) => ({ ...f, attachmentUrl: url })));
          }}
        />
      </label>
      {form.attachmentUrl && (
        <a href={form.attachmentUrl} target="_blank" rel="noopener noreferrer" className="col-span-2 sm:col-auto text-xs text-primary-700 self-center break-all">
          View attachment
        </a>
      )}
    </div>
    <div className="flex items-center gap-3">
      <button onClick={onSave} disabled={saving} className="btn-primary text-sm py-1.5 px-3 flex-1 sm:flex-none">
        {saving ? 'Saving…' : 'Save lesson'}
      </button>
      <button onClick={onCancel} className="text-sm text-gray-600 hover:text-gray-900 shrink-0">Cancel</button>
    </div>
  </div>
);

export default AdminAcademy;
